import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarExport,
    gridClasses
} from '@mui/x-data-grid';
import { GetList, GetById, AddItem, UpdateItem, DeleteItem } from '../../REST/crud';
import {
    Button,
    TextField,
    IconButton,
    Slide,
    Typography,
    Box,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    NativeSelect,
    FormHelperText,
    Backdrop,
    CircularProgress,
    Dialog,
    Chip,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CodeIcon from '@mui/icons-material/Code';
import PreviewIcon from '@mui/icons-material/Preview';
import EmailIcon from '@mui/icons-material/Email';
import MessageIcon from '@mui/icons-material/Message';
import DescriptionIcon from '@mui/icons-material/Description';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import config from '../../config';
import DOMPurify from 'dompurify';
import ErrorBoundary from '../../themes/components/ErrorBoundary';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import Action from '../../themes/components/Action';
import WorkflowDialogShell from '../../themes/components/WorkflowDialogShell';
import { handleErr } from '../../helpers/errors';
import Access from '../../Utils/authorize';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function CustomToolbar() {
    return (
        <GridToolbarContainer className={gridClasses.toolbarContainer}>
            <GridToolbarExport />
        </GridToolbarContainer>
    );
}

// ─── Module field definitions (mirrors backend helper.js getFields exactly) ───
// Format must match GetNoticeFields: groups with { name, id } field objects
const MODULE_FIELDS = {
    Waitlist: [
        { name: 'Waitlist', id: 'Waitlist', fields: [
            { name: 'FirstName', id: 'firstName' }, { name: 'LastName', id: 'lastName' },
            { name: 'Phone', id: 'phone' }, { name: 'Email', id: 'email' },
            { name: 'Organization', id: 'organization' }, { name: 'Queue', id: 'queue' },
            { name: 'Address', id: 'address' }, { name: 'City', id: 'city' },
            { name: 'State', id: 'state' }, { name: 'Zip', id: 'zip' },
            { name: 'Notes', id: 'notes' }, { name: 'CreatedAt', id: 'createdAt' },
        ]},
        { name: 'Receipt', id: 'Receipt', fields: [
            { name: 'ReceiptId', id: 'uid' }, { name: 'InvoiceNumber', id: 'invoiceNumber' },
            { name: 'Status', id: 'status' },
        ]},
        { name: 'LineItems', id: 'LineItems', fields: [
            { name: 'Name', id: 'name' }, { name: 'Description', id: 'description' },
            { name: 'Total', id: 'total' },
        ]},
    ],
    Invoices: [
        { name: 'Invoices', id: 'Invoices', fields: [
            { name: 'Name', id: 'name' }, { name: 'TenantName', id: 'tenantName' },
            { name: 'Description', id: 'description' }, { name: 'InvoiceNumber', id: 'invoiceNumber' },
            { name: 'InvoiceDate', id: 'invoiceDate' }, { name: 'DueDate', id: 'dueDate' },
            { name: 'PaymentDate', id: 'paymentDate' }, { name: 'Status', id: 'status' },
            { name: 'CreatedAt', id: 'createdAt' },
        ]},
    ],
    Agreements: [
        { name: 'Agreements', id: 'Agreements', fields: [
            { name: 'Title', id: 'title' }, { name: 'AgreementNum', id: 'agreementNum' },
            { name: 'Description', id: 'description' }, { name: 'StartDate', id: 'startDate' },
            { name: 'EndDate', id: 'endDate' }, { name: 'Status', id: 'status' },
            { name: 'LeaseTerms', id: 'leaseTerms' }, { name: 'NextInvoiceDate', id: 'nextinvoicedate' },
            { name: 'FundCode', id: 'fundCode' }, { name: 'OrgCode', id: 'orgCode' },
            { name: 'CreatedAt', id: 'createdAt' },
        ]},
    ],
    Company: [
        { name: 'Company', id: 'Company', fields: [
            { name: 'Name', id: 'name' }, { name: 'Email', id: 'email' },
            { name: 'Phone', id: 'phone' }, { name: 'Description', id: 'description' },
            { name: 'Status', id: 'status' }, { name: 'Address1', id: 'address1' },
            { name: 'Address2', id: 'address2' }, { name: 'City', id: 'city' },
            { name: 'State', id: 'state' }, { name: 'Zip', id: 'zip' },
            { name: 'ContactName', id: 'contactname' }, { name: 'ContactPhone', id: 'contactphone' },
            { name: 'CreatedAt', id: 'createdAt' },
        ]},
    ],
    Insurance: [
        { name: 'Insurance', id: 'Insurance', fields: [
            { name: 'MaxCoverage', id: 'maxCoverageAmount' }, { name: 'IsApproved', id: 'isApproved' },
            { name: 'StartDate', id: 'startDate' }, { name: 'EndDate', id: 'endDate' },
            { name: 'Status', id: 'status' }, { name: 'CreatedAt', id: 'createdAt' },
        ]},
    ],
    Tiedown: [
        { name: 'Tiedown', id: 'Tiedown', fields: [
            { name: 'FirstName', id: 'firstName' }, { name: 'LastName', id: 'lastName' },
            { name: 'BusinessName', id: 'businessName' }, { name: 'Phone', id: 'phone' },
            { name: 'Email', id: 'email' }, { name: 'ArrivalDate', id: 'beginDate' },
            { name: 'DepartureDate', id: 'endDate' }, { name: 'AircraftType', id: 'airCraftType' },
            { name: 'AircraftNumber', id: 'airCraftNum' }, { name: 'Amount', id: 'amount' },
            { name: 'CreatedAt', id: 'createdAt' },
        ]},
    ],
    Landing: [
        { name: 'Landing', id: 'Landing', fields: [
            { name: 'AircraftNumber', id: 'nnumber' }, { name: 'Wingspan', id: 'wingspan' },
            { name: 'InvoiceNumber', id: 'invoiceNumber' }, { name: 'InvoiceDate', id: 'invoiceDate' },
            { name: 'RecipientName', id: 'recipientname' }, { name: 'RecipientEmail', id: 'recipientemail' },
            { name: 'RecipientAddress', id: 'recipientaddress' }, { name: 'DueDate', id: 'dueDate' },
            { name: 'Status', id: 'status' }, { name: 'Charges', id: 'charges' },
            { name: 'CreatedAt', id: 'createdAt' },
        ]},
    ],
    Accounts: [
        { name: 'Accounts', id: 'Accounts', fields: [
            { name: 'Email', id: 'email' }, { name: 'Access', id: 'access' },
            { name: 'Admin', id: 'admin' }, { name: 'Enable', id: 'enable' },
            { name: 'Link', id: 'link' }, { name: 'CreatedAt', id: 'createdAt' },
        ]},
    ],
    IncidentReports: [
        { name: 'IncidentReports', id: 'IncidentReports', fields: [
            { name: 'Status', id: 'status' }, { name: 'CreatedAt', id: 'createdAt' },
        ]},
    ],
};

// Returns grouped array for the SFDT iframe (Select Field State → Select Field)
const getModuleFields = (module) => MODULE_FIELDS[module] || [];

// Returns flat list of all field names for the HTML chip hints
const getModuleFieldsFlat = (module) =>
    (MODULE_FIELDS[module] || []).flatMap(g => g.fields.map(f => f.name));

// ─── SFDT iframe editor (used for Message Templates with visual editor) ────────
function DocumentEditor({ label, value, onChange, fields = [] }, ref) {
    const [data, setData] = useState(value);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const frameView = React.useRef(null);

    useEffect(() => { if (!open) onChange(data); }, [open, data, onChange]);
    useEffect(() => { setData(value); }, [value]);

    useEffect(() => {
        if (!open) return;
        const handleSubmit = (d) => { setData(d); setOpen(false); };
        const handleClose = () => { setOpen(false); setData(value); };
        const messageHandler = (message) => {
            if (message.origin === config.REACT_APP_TEMPLATE_URL) {
                if (message.data === 'Initialized' && frameView.current?.contentWindow) {
                    setLoading(false);
                    frameView.current.contentWindow.postMessage(
                        { render: 'Fields', fields, content: value }, '*'
                    );
                } else if (message.data === 'Close') handleClose();
                else handleSubmit(message.data);
            }
        };
        window.addEventListener('message', messageHandler);
        return () => window.removeEventListener('message', messageHandler);
    }, [value, open, fields]);

    return (
        <div>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Button variant="outlined" fullWidth onClick={() => { setOpen(true); setLoading(true); }}>
                {label}
            </Button>
            <Dialog fullScreen open={open} keepMounted={false} TransitionComponent={Transition}>
                <iframe
                    src={config.REACT_APP_TEMPLATE_URL}
                    ref={frameView}
                    frameBorder="0"
                    marginHeight="0"
                    marginWidth="0"
                    width="100%"
                    height="100%"
                    title="Template Editor"
                    scrolling="auto"
                />
            </Dialog>
        </div>
    );
}
const DocumentEditorRef = forwardRef(DocumentEditor);

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function CommunicationTemplates() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editKind, setEditKind] = useState(null); // 'email' | 'message'
    const [refresh, setRefresh] = useState(false);
    const [pageSize, setPageSize] = useState(10);

    const canEdit = Access.isAdmin('Settings');
    const canDelete = Access.isAdmin('Settings');

    const toggleRefresh = () => setRefresh(r => !r);

    // Load both collections and merge into one list
    useEffect(() => {
        (async () => {
            const [htmlResult, msgResult] = await Promise.all([
                GetList('HtmlTemplate'),
                GetList('MessageTemplate', { limit: 1000 })
            ]);

            const htmlRows = (htmlResult.data?.data || htmlResult.data || []).map(e => ({
                id: e._id,
                _kind: 'email',
                ...e
            }));
            const msgRows = (msgResult.data?.data || msgResult.data || []).map(e => ({
                id: e._id,
                _kind: 'message',
                ...e
            }));

            setRows([...htmlRows, ...msgRows]);
        })();
    }, [refresh]);

    const handleDelete = async (id, kind) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;
        const apiType = kind === 'email' ? 'HtmlTemplate' : 'MessageTemplate';
        await DeleteItem(apiType, id);
        toggleRefresh();
    };

    const handleEdit = (id, kind) => {
        setEditId(id);
        setEditKind(kind);
        setOpen(true);
    };

    const handleAdd = () => {
        setEditId(null);
        setEditKind(null); // user will choose kind in the form
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditId(null);
        setEditKind(null);
        toggleRefresh();
    };

    const columns = [
        {
            field: 'name',
            headerName: 'Template Name',
            flex: 0.35,
            minWidth: 180
        },
        {
            field: 'subject',
            headerName: 'Subject',
            flex: 0.4,
            minWidth: 200
        },
        {
            field: '_kind',
            headerName: 'Type',
            flex: 0.15,
            minWidth: 120,
            renderCell: (params) =>
                params.value === 'email' ? 'Standard' : 'Custom'
        },
        {
            field: 'modelType',
            headerName: 'Linked Module',
            flex: 0.2,
            minWidth: 150,
            renderCell: (params) =>
                params.row._kind === 'email'
                    ? <span style={{ color: '#555', fontStyle: 'italic' }}>N/A</span>
                    : (params.value || '—')
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.1,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => (
                <>
                    {canEdit && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(params.row.id, params.row._kind); }}>
                            <EditIcon />
                        </IconButton>
                    )}
                    {canDelete && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(params.row.id, params.row._kind); }}>
                            <DeleteIcon />
                        </IconButton>
                    )}
                </>
            )
        }
    ];

    return (
        <PageShell>
            <PageHeader
                title="Communication Templates"
                back
                onBack={() => navigate(-1)}
                actions={
                    canEdit && (
                        <Action
                            primary
                            icon={<AddIcon />}
                            label="Add Template"
                            onClick={handleAdd}
                        />
                    )
                }
            />
            <PageContent>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectionOnClick
                    autoHeight
                    components={{ Toolbar: CustomToolbar }}
                />
            </PageContent>
            <UnifiedTemplateForm
                open={open}
                onClose={handleClose}
                editId={editId}
                editKind={editKind}
            />
        </PageShell>
    );
}

// ─── UNIFIED FORM ─────────────────────────────────────────────────────────────
// Handles both Email Templates (HtmlTemplate API) and Message Templates (MessageTemplate API)
// When adding new: user picks kind via toggle
// When editing: kind is locked to what was saved
function UnifiedTemplateForm({ open, onClose, editId, editKind }) {
    // 'email' | 'message' — null means user hasn't chosen yet (new only)
    const [kind, setKind] = useState(editKind || 'email');
    const [htmlTab, setHtmlTab] = useState(0); // 0=Code, 1=Preview

    // Sync kind when dialog opens
    useEffect(() => {
        if (open) {
            setKind(editKind || 'email');
            setHtmlTab(0);
        }
    }, [open, editKind]);

    // ── Schema varies slightly by kind ──────────────────────────────────────
    const schema = yup.object().shape({
        name: yup.string().required('Template name is required'),
        subject: yup.string().required('Subject is required'),
        // Email-only: body description
        body: yup.string().when('$kind', {
            is: 'email',
            then: (s) => s.required('Body description is required'),
            otherwise: (s) => s.notRequired()
        }),
        // HTML content — required for HTML type
        content: yup.string().when('templateType', {
            is: (v) => !v || v === 'HTML',
            then: (s) => s.required('HTML content is required'),
            otherwise: (s) => s.notRequired()
        }),
        // Message-only: templateType + sfdtTemplate
        templateType: yup.string().oneOf(['HTML', 'SFDT']).default('HTML'),
        sfdtTemplate: yup.string().when('templateType', {
            is: 'SFDT',
            then: (s) => s.required('Visual editor content is required'),
            otherwise: (s) => s.notRequired()
        })
    });

    const { control, handleSubmit, setError, formState: { errors }, reset, watch, setValue } = useForm({
        defaultValues: {
            name: '',
            subject: '',
            body: '',
            content: '',
            templateType: 'HTML',
            sfdtTemplate: '',
            modelType: ''
        },
        context: { kind },
        resolver: yupResolver(schema)
    });

    const watchContent = watch('content');
    const templateType = watch('templateType');
    const watchModelType = watch('modelType');
    const moduleFields = getModuleFields(watchModelType);          // grouped — for SFDT iframe
    const moduleFieldsFlat = getModuleFieldsFlat(watchModelType);  // flat — for HTML chips

    const htmlEditorRef = useRef(null);

    // Insert «FieldName» at the textarea cursor position (falls back to clipboard copy)
    const insertField = (fieldName) => {
        const token = `\u00AB${fieldName}\u00BB`;
        const el = htmlEditorRef.current;
        if (el && htmlTab === 0) {
            const start = el.selectionStart ?? 0;
            const end   = el.selectionEnd   ?? 0;
            const current = el.value || '';
            const newVal = current.slice(0, start) + token + current.slice(end);
            setValue('content', newVal, { shouldDirty: true });
            // Restore cursor position after React re-render
            setTimeout(() => {
                el.selectionStart = start + token.length;
                el.selectionEnd   = start + token.length;
                el.focus();
            }, 0);
        } else {
            navigator.clipboard?.writeText(token);
        }
    };

    const _defaults = { name: '', subject: '', body: '', content: '', templateType: 'HTML', sfdtTemplate: '', modelType: '' };

    // Load existing data when editing
    useEffect(() => {
        if (!open) return;
        (async () => {
            if (editId && editKind) {
                const apiType = editKind === 'email' ? 'HtmlTemplate' : 'MessageTemplate';
                const resp = await GetById(apiType, editId);
                const data = resp.data;
                reset({
                    name: data.name || '',
                    subject: data.subject || '',
                    body: data.body || '',
                    content: data.content || '',
                    templateType: data.templateType || 'HTML',
                    sfdtTemplate: data.sfdtTemplate || '',
                    modelType: data.modelType || ''
                });
            } else {
                reset(_defaults);
            }
        })();
    }, [editId, editKind, open]);

    const decodeIfEscaped = (input) => {
        if (!input || typeof input !== 'string') return '';
        if (input.includes('&lt;') || input.includes('&amp;lt;')) {
            const txt = document.createElement('textarea');
            txt.innerHTML = input;
            return txt.value;
        }
        return input;
    };

    const onSubmit = async (data) => {
        try {
            const apiType = kind === 'email' ? 'HtmlTemplate' : 'MessageTemplate';
            const payload = {
                name: data.name,
                subject: data.subject,
                body: data.body,
                content: data.content,
                templateType: data.templateType,
                sfdtTemplate: data.sfdtTemplate,
                modelType: kind === 'message' ? (data.modelType || null) : null
            };

            if (editId) {
                await UpdateItem(apiType, editId, payload);
            } else {
                await AddItem(apiType, payload);
            }
            onClose();
        } catch (err) {
            // Friendly message for duplicate template name (MongoDB unique constraint)
            const msg = err?.response?.data?.message || err?.message || '';
            if (msg.includes('duplicate key') || msg.includes('E11000') || msg.includes('name_1')) {
                setError('name', { message: 'A template with this name already exists. Please choose a different name.' });
            } else {
                handleErr(err, setError);
            }
        }
    };

    const isAdding = !editId;
    const title = editId
        ? `Edit ${kind === 'email' ? 'Email' : 'Message'} Template`
        : 'Add Template';

    return (
        <WorkflowDialogShell
            open={open}
            onClose={onClose}
            onSave={handleSubmit(onSubmit)}
            title={title}
            transitionComponent={Transition}
            maxWidth="lg"
            fullWidth
        >
            <div className="container-fluid">

                {/* ── Kind selector — only shown when adding new ── */}
                {isAdding && (
                    <div className="row">
                        <div className="col-lg-6 col-md-8 col-sm-12">
                            <FormControl variant='standard' margin='dense' fullWidth>
                                <InputLabel shrink>Template Type</InputLabel>
                                <NativeSelect
                                    value={kind}
                                    onChange={(e) => { if (e.target.value) { setKind(e.target.value); reset(_defaults); } }}
                                >
                                    <option value="email">Standard</option>
                                    <option value="message">Custom</option>
                                </NativeSelect>
                                <FormHelperText>
                                    {kind === 'email'
                                        ? 'Used for automated system emails (inspections, agreements, invoices, etc.)'
                                        : 'Used when manually sending alerts or messages to users'}
                                </FormHelperText>
                            </FormControl>
                        </div>
                    </div>
                )}

                {/* ── Common fields ── */}
                <div className="row">
                    <div className="col-12">
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    variant="standard"
                                    margin="dense"
                                    fullWidth
                                    label="Template Name"
                                    placeholder={kind === 'email' ? 'e.g., INSPECTION, AGREEMENT, INVOICE' : 'e.g., Welcome Email, Monthly Newsletter'}
                                    required
                                    error={!!errors.name}
                                    helperText={
                                        errors.name?.message ||
                                        (editId && kind === 'email'
                                            ? 'Standard template names are locked — the backend uses this name to find the template.'
                                            : undefined)
                                    }
                                    disabled={!!(editId && kind === 'email')}
                                    InputProps={editId && kind === 'email' ? { readOnly: true } : undefined}
                                />
                            )}
                        />

                        <Controller
                            name="subject"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    variant="standard"
                                    margin="dense"
                                    fullWidth
                                    label="Email Subject"
                                    placeholder="e.g., New Inspection Created"
                                    required
                                    error={!!errors.subject}
                                    helperText={errors.subject?.message}
                                />
                            )}
                        />

                        {/* ── Email-only: body description ── */}
                        {kind === 'email' && (
                            <Controller
                                name="body"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        variant="standard"
                                        margin="dense"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label="Body Description"
                                        placeholder="Brief description of the email purpose"
                                        required
                                        error={!!errors.body}
                                        helperText={errors.body?.message}
                                    />
                                )}
                            />
                        )}

                        {/* ── Linked module — only for Custom (message) templates, not Standard ── */}
                        {kind === 'message' && (
                            <Controller
                                name="modelType"
                                control={control}
                                render={({ field }) => (
                                    <FormControl variant="standard" margin="dense" fullWidth>
                                        <InputLabel shrink>Linked Module (optional)</InputLabel>
                                        <NativeSelect {...field}>
                                            <option value="">Not linked — general template</option>
                                            <option value="Waitlist">Waitlist</option>
                                            <option value="Invoices">Invoices</option>
                                            <option value="Agreements">Agreements</option>
                                            <option value="Company">Company</option>
                                            <option value="Insurance">Insurance</option>
                                            <option value="Tiedown">Tiedown</option>
                                            <option value="Landing">Landing</option>
                                            <option value="Accounts">Accounts</option>
                                            <option value="IncidentReports">Incident Reports</option>
                                        </NativeSelect>
                                        <FormHelperText>
                                            Link this template to a module so staff can manually send it to specific records
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        )}

                        {/* ── Editor type selector — available for all template kinds ── */}
                        <Controller
                            name="templateType"
                            control={control}
                            render={({ field }) => (
                                <FormControl variant="standard" margin="dense" fullWidth>
                                    <InputLabel shrink>Editor Type</InputLabel>
                                    <NativeSelect {...field} disabled={!!editId}>
                                        <option value="HTML">HTML Code Editor</option>
                                        <option value="SFDT">Visual Editor (SFDT)</option>
                                    </NativeSelect>
                                    <FormHelperText>
                                        HTML: write raw HTML code &nbsp;|&nbsp; Visual: use the drag-and-drop editor
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </div>
                </div>

                {/* ── HTML content editor — shown when templateType is HTML ── */}
                {templateType === 'HTML' && (
                    <Box sx={{ mt: 3, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            HTML Content *
                        </Typography>
                        {watchModelType && moduleFieldsFlat.length > 0 ? (
                            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                    Available variables for <strong>{watchModelType}</strong> — click to insert:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {moduleFieldsFlat.map(name => (
                                        <Chip
                                            key={name}
                                            label={`«${name}»`}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => insertField(name)}
                                            sx={{ fontFamily: 'monospace', cursor: 'pointer', fontSize: '0.72rem' }}
                                        />
                                    ))}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {htmlTab === 0
                                        ? <>Click a chip to insert it at the cursor in the editor below.</>                                        
                                        : <>Switch to <strong>HTML Code</strong> tab, place your cursor, then click a chip to insert.</>}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Select a <strong>Linked Module</strong> above to see available variables for that module.
                                Variables use the <code>«VariableName»</code> syntax (angle quotes).
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DescriptionIcon />}
                                onClick={() => window.open('https://test.revjet360.com/', '_blank')}
                            >
                                Open Document Converter
                            </Button>
                        </Box>

                        <Tabs value={htmlTab} onChange={(_, v) => setHtmlTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                            <Tab icon={<CodeIcon />} iconPosition="start" label="HTML Code" />
                            <Tab icon={<PreviewIcon />} iconPosition="start" label="Preview" />
                        </Tabs>

                        <Controller
                            name="content"
                            control={control}
                            render={({ field }) => (
                                <>
                                    {htmlTab === 0 && (
                                        <TextField
                                            {...field}
                                            inputRef={htmlEditorRef}
                                            fullWidth
                                            multiline
                                            rows={18}
                                            variant="outlined"
                                            placeholder="<html>...<body>...</body></html>"
                                            error={!!errors.content}
                                            helperText={errors.content?.message}
                                            sx={{
                                                '& textarea': {
                                                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                                    fontSize: '13px',
                                                    lineHeight: '1.6'
                                                }
                                            }}
                                        />
                                    )}
                                    {htmlTab === 1 && (
                                        <Box sx={{
                                            border: '2px solid #e0e0e0',
                                            borderRadius: 2,
                                            p: 3,
                                            minHeight: 400,
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                            overflow: 'auto'
                                        }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#666', fontStyle: 'italic' }}>
                                                Email preview (as the recipient will see it)
                                            </Typography>
                                            <ErrorBoundary>
                                                <div dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(decodeIfEscaped(field.value || ''))
                                                }} />
                                            </ErrorBoundary>
                                        </Box>
                                    )}
                                </>
                            )}
                        />
                    </Box>
                )}

                {/* ── Visual editor — shown when templateType is SFDT ── */}
                {templateType === 'SFDT' && (
                    <Box sx={{ mt: 3, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Visual Template Editor
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Open the visual editor to design the template. Changes are saved as SFDT format.
                        </Typography>
                        {watchModelType && moduleFieldsFlat.length > 0 && (
                            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                                    Available fields for <strong>{watchModelType}</strong> — use the Insert Field toolbar inside the editor:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {moduleFieldsFlat.map(name => (
                                        <Chip key={name} label={name} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        <Controller
                            name="sfdtTemplate"
                            control={control}
                            render={({ field }) => (
                                <DocumentEditorRef
                                    label="Open Visual Editor"
                                    value={field.value}
                                    onChange={(val) => setValue('sfdtTemplate', val)}
                                    fields={moduleFields}
                                />
                            )}
                        />
                        {errors.sfdtTemplate && (
                            <Typography color="error" variant="caption">{errors.sfdtTemplate.message}</Typography>
                        )}
                    </Box>
                )}
            </div>
        </WorkflowDialogShell>
    );
}
